import { Control, FieldPath, FieldValues } from 'react-hook-form';
import { BaseCurrencyInput } from '../form/currency-input';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../form/form';
import { CurrencyAdjustmentPopover } from '@/components/ui/field/currency-adjustment';

interface CurrencyFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  label?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  allowNegativeValue?: boolean;
  className?: string;
  placeholder?: string;
  showAdjustment?: boolean;
}

export function CurrencyField<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  disabled,
  allowNegativeValue,
  className,
  placeholder,
  showAdjustment = false,
}: CurrencyFieldProps<TFieldValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          {label && <FormLabel>{label}</FormLabel>}
          <div className="flex items-center gap-2">
            <FormControl>
              <BaseCurrencyInput
                placeholder={placeholder}
                value={field.value}
                onChange={(val) => field.onChange(val)}
                allowNegativeValue={allowNegativeValue}
                disabled={disabled}
                required={required}
                className={className}
              />
            </FormControl>
            {showAdjustment && (
              <CurrencyAdjustmentPopover
                onAdjust={(adjustment) => {
                  const currentValue = parseFloat(field.value) || 0;
                  const adjustmentValue = typeof adjustment === 'string' ? parseFloat(adjustment) : 0;
                  const newValue = currentValue + adjustmentValue;
                  field.onChange(newValue.toString());
                }}
              />
            )}
          </div>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage>{fieldState.error?.message}</FormMessage>
        </FormItem>
      )}
    />
  );
}
